import RestService from "model/service/dataStorage/RestService";
import IRestServiceOptions from "../../interface/api/IRestServiceOptions";
import IRestServiceCollectionResponse from "../../interface/api/IRestServiceCollectionResponse";
import IOrganigrammeNode from "../../interface/company/IOrganigrammeNode";
import _ from "underscore"
import {API_FILTER_TYPE} from "../../constants/ApiConstant";
import IRestServiceChoiceListResponse, {
    IRestServiceChoiceList
} from "../../interface/api/IRestServiceChoiceListResponse";
import {Key} from "react";
import Utils from "../../../utils";

interface IRestCompanyStructureServiceCollectionResponse extends IRestServiceCollectionResponse {
    results: Array<IOrganigrammeNode>
}

export type IRestCompanyStructureAccept = "company" | "employee" | "unit" | "job" | "node"

const COLLECTION = 'organigramme'

interface ICompanyStructureService {
    collectionList(options?: IRestServiceOptions): Promise<IRestCompanyStructureServiceCollectionResponse>,

    loadTree(options?: IRestServiceOptions): Promise<IRestCompanyStructureServiceCollectionResponse>,

    loadByPath(path: any): Promise<IOrganigrammeNode>,

    getData(collection: string, options?: IRestServiceOptions): Promise<IRestCompanyStructureServiceCollectionResponse>,

    loadById(id: number): Promise<IOrganigrammeNode | undefined>,

    loadByParentId(id: number | string | number[] | string[]): Promise<Array<IOrganigrammeNode>>,

    loadByChildId(id: number | string): Promise<Array<IOrganigrammeNode>>,

    loadOne(id: number | string, options?: IRestServiceOptions): Promise<IRestCompanyStructureServiceCollectionResponse>,

    getAllChildren(parent: IOrganigrammeNode): Promise<Array<IOrganigrammeNode>>,

    walkTree(items: Array<IOrganigrammeNode>, callback: Function): void,

    collectionCreate(data: any, options?: IRestServiceOptions): Promise<IOrganigrammeNode>,

    resourceUpdate(id: number | string, data: any, options?: IRestServiceOptions): Promise<IOrganigrammeNode>,

    resourceDelete(id: number | string): Promise<void>,

    loadAllChildrenById(id?: number | string | number[] | string[], options?: IRestServiceOptions): Promise<IRestCompanyStructureServiceCollectionResponse>,

    loadSharedChildrenOnly(ids?: Key[], options?: IRestServiceOptions): Promise<IRestCompanyStructureServiceCollectionResponse>,

    loadSharedChildrenOnlyChoices(ids?: Key[], presenter?: string, options?: IRestServiceOptions): Promise<IRestServiceChoiceListResponse>,

    loadAllStructureChoicesById(id?: number | string | number[] | string[], accept?: IRestCompanyStructureAccept, leafId?: boolean, options?: IRestServiceOptions): Promise<IRestServiceChoiceListResponse>,

    getTitle(node: IOrganigrammeNode): string

    buildEmptyNode(parent?: IOrganigrammeNode): IOrganigrammeNode
}

const CompanyStructureService: ICompanyStructureService = {
    loadTree: function (options) {
        const params = {
            limit: 0,
            filters: {
                0: {
                    field: 'parent',
                    type: API_FILTER_TYPE.IS_NULL
                },
            },
            ...options
        }
        return CompanyStructureService.getData(COLLECTION, params);
    },
    loadByPath: function (path: Array<number>) {
        return CompanyStructureService.getData(COLLECTION).then(data => {
            return new Promise((resolve, reject) => {
                let items = data.results
                let item: IOrganigrammeNode | undefined;
                path.forEach(id => {
                    if (items) {
                        item = _.findWhere(items, {id: id})
                        if (item) {
                            items = item.children
                        } else {
                            items = []
                        }
                    }
                })
                if (item) {
                    resolve(item)
                } else {
                    reject(item)
                }
            });
        })
    },
    getData: function (collection: string, options?: any) {
        if (!options) {
            options = {}
        }
        options.cache = false
        return RestService.collectionList(collection, options) as Promise<IRestCompanyStructureServiceCollectionResponse>
    },
    loadById: function (id: number) {
        const params = {
            limit: 0,
            filters: {
                0: {
                    field: 'id',
                    type: API_FILTER_TYPE.EQUAL,
                    value: id.toString()
                },
            },
        }
        return CompanyStructureService.getData(COLLECTION, params).then(data => {
            let result: IOrganigrammeNode | undefined;
            if (data.results !== undefined) {
                CompanyStructureService.walkTree(data.results, (item: IOrganigrammeNode) => {
                    if (item.id === id) {
                        result = item
                        return false
                    }
                })
                return result
            }
        })
    },
    loadByParentId: function (parentId: number | string | number[] | string[]) {
        const params = {
            limit: 0,
            filters: {
                0: {
                    field: 'parent',
                    type: typeof parentId === 'object' ? API_FILTER_TYPE.IN : API_FILTER_TYPE.EQUAL,
                    value: parentId
                },
            },
        }
        return CompanyStructureService.getData(COLLECTION, params).then(data => {
            if (data.results !== undefined) {
                return data.results;
            } else {
                return [];
            }
        })
    },
    loadByChildId: function (childId: number) {
        return CompanyStructureService.getData('organigramme-action/get-parent-tree/' + childId).then(data => {
            if (data.results !== undefined) {
                return data.results;
            } else {
                return [];
            }
        })
    },
    loadOne: function (id, options) {
        return CompanyStructureService.getData('organigramme-action/get-one/' + id, options) as Promise<IRestCompanyStructureServiceCollectionResponse>
    },
    getAllChildren: function (parent: IOrganigrammeNode) {
        return new Promise(resolve => {
            let output: Array<IOrganigrammeNode> = []
            CompanyStructureService.walkTree(parent.children, (node: IOrganigrammeNode) => {
                output.push(node)
            })
            resolve(output)
        })
    },
    loadAllChildrenById: function (id, options) {
        if (!id || (Array.isArray(id) && id.length === 0)) {
            return CompanyStructureService.collectionList({limit: 0, ...options}).then(data => {
                return data
            })
        }
        const ids = typeof id === 'object' ? id : [id]
        let promises = [] as Promise<IRestCompanyStructureServiceCollectionResponse>[]
        ids.forEach(_id => {
            promises.push(CompanyStructureService.getData('organigramme-action/get-all-children/' + _id, {limit: 0, ...options}).then(data => {
                return data
            }))
        })
        return Promise.all(promises).then(results => {
            let output: IRestCompanyStructureServiceCollectionResponse = {...results[0]}
            results.forEach(result => {
                output = {...result, results: output.results.concat(result.results), count: output.count + result.count}
            })
            return output
        })
    },
    loadSharedChildrenOnly: function (ids , options) {
        return RestService.collectionList('organigramme-action/get-shared-children-only', {
            ...options,
            ids: ids
        }) as Promise<IRestCompanyStructureServiceCollectionResponse>
    },
    loadSharedChildrenOnlyChoices: function (ids, presenter = 'default', options) {
        return RestService.collectionList('organigramme-action/get-shared-children-only-choices/' + presenter, {
            ...options,
            ids: ids
        }) as unknown as Promise<IRestServiceChoiceListResponse>
    },
    loadAllStructureChoicesById: function (id, accept?: IRestCompanyStructureAccept, leafId = false, options?: IRestServiceOptions) {
        if (!id) {
            return RestService.collectionList('organigramme-action/get-all-structure-choices' + (id ? '/' + id : ''), {
                ...options,
                accept: accept === 'node' ? '' : accept, ...(leafId ? {leafId} : {})
            })
                .then(response => {
                    return response as unknown as IRestServiceChoiceListResponse
                })
                .then(data => {
                    return data
                })
        }
        const ids = typeof id === 'object' ? id : [id]
        let promises = [] as Promise<IRestServiceChoiceList>[]
        ids.forEach(_id => {
            promises.push(RestService.collectionList('organigramme-action/get-all-structure-choices/' + _id, {
                accept,
                ...options?.contentTypePresenter,
                ...(leafId ? {leafId} : {})
            })
                .then(response => {
                    return response as unknown as IRestServiceChoiceListResponse
                })
                .then(data => {
                    return data.results || {}
                }))
        })
        return Promise.all(promises).then(results => {
            let output: IRestServiceChoiceListResponse = {results: {}, limit: 0, page: 0, count: 0, totalCount: 0}
            results.forEach(result => {
                output = {...output, results: {...output.results, ...result}}
            })
            return output
        })
    },
    walkTree: function (items: Array<IOrganigrammeNode>, callback) {
        if (items.filter((node: IOrganigrammeNode) => {
            return node.id !== undefined;
        }).length === 0) {
            return [];
        }
        items.forEach(item => {
            const result = callback(item)
            if (result === false) {
                return false
            }
            if (item.children !== undefined) {
                CompanyStructureService.walkTree(item.children, callback)
            }
        })
    },
    collectionCreate: function (data: any) {
        return RestService.collectionCreate(COLLECTION, data) as Promise<IOrganigrammeNode>
    },
    resourceUpdate: function (id: number | string, data: any) {
        return RestService.resourceUpdate(COLLECTION, id, data) as Promise<IOrganigrammeNode>
    },
    resourceDelete: function (id: number | string) {
        return RestService.resourceDelete(COLLECTION, id) as Promise<void>
    },
    collectionList(options?: IRestServiceOptions): Promise<IRestCompanyStructureServiceCollectionResponse> {
        return RestService.collectionList(COLLECTION, options as unknown as IRestServiceOptions) as Promise<IRestCompanyStructureServiceCollectionResponse>
    },
    getTitle(node: IOrganigrammeNode): string {
        if (node.employee) {
            return node.employee!.fullName!
        } else if (node.unit) {
            return node.unit!.name!
        } else if (node.company) {
            return node.company.name!
        } else if (node.job) {
            return node.job?.name!
        }
        return 'Nový uzel'
    },
    buildEmptyNode(parent?: IOrganigrammeNode): IOrganigrammeNode {
        const path = parent ? [...parent.path, parent.uuid] : []
        return {
            uuid: Utils.uuid(),
            parent: parent?.uuid,
            children: [],
            path: path,
            pathLabels: [],
            fromRelations: [],
            toRelations: [],
            lvl: 0
        };
    }
}

export default CompanyStructureService
